import type { IUser, IOrganization } from 'types/auth/types';
import type { SwitcherAccount } from '@features/account-view/useAccountSwitcher';

export type Organization = {
  _id: string;
  company_name?: string;
  logo?: { url?: string };
};

/** Rows the list will draw before it asks to be searched instead. */
export const orgListLimit = 10;

/** The one in use first, the rest by name — the order the list reads in. */
export function getOrganizations(
  user: IUser | null,
  currentOrg?: string,
  currentOrgInfo?: IOrganization,
) {
  const result = (user?.organizations || [])
    .filter(x => x._id !== currentOrg)
    .sort((a, b) => (a.company_name ?? '').localeCompare(b.company_name ?? ''));

  if (currentOrgInfo) {
    result.unshift(currentOrgInfo);
  }

  return result;
}

export function filterOrganizations(orgSearch: string, orgs: IOrganization[]) {
  let result = orgs;
  if (orgSearch) {
    result = orgs.filter(x =>
      x.company_name?.toLowerCase().includes(orgSearch.toLowerCase()),
    );
  }
  return result.slice(0, orgListLimit);
}

/** One account row with its divisions, or (no account) one standalone org. */
export type OrgGroup = {
  account?: Pick<
    SwitcherAccount,
    'id' | 'name' | 'accountLevel' | 'accountViewEnabled' | 'logoUrl'
  >;
  divisions: Organization[];
};

const byName = (a: Organization, b: Organization) =>
  (a.company_name ?? '').localeCompare(b.company_name ?? '');

const has = (text: string | undefined, search: string) =>
  !!text?.toLowerCase().includes(search.toLowerCase());

/**
 * The switcher's list: a group per account (by name) holding the user's
 * divisions of it (by name), then each other org on its own, in the order
 * given. A search matches division and account names; an account-name match
 * keeps the whole group. At most `orgListLimit` division rows are kept, but
 * the group of `currentId` is never cut. An account the user holds no
 * division of is left out (nothing to switch to), and an org belongs to the
 * first account that lists it.
 */
export function groupOrganizations(
  orgs: Organization[],
  accounts: SwitcherAccount[],
  search: string,
  currentId?: string,
): OrgGroup[] {
  const placed = new Set<string>();
  const accountGroups: OrgGroup[] = [...accounts]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ divisionIds, ...account }) => {
      const divisions = orgs
        .filter(o => divisionIds.includes(o._id) && !placed.has(o._id))
        .sort(byName);
      for (const o of divisions) placed.add(o._id);
      return { account, divisions };
    });
  const standalone = orgs.filter(o => !placed.has(o._id));

  const matching = [
    ...accountGroups.map(g =>
      !search || has(g.account?.name, search)
        ? g
        : {
            ...g,
            divisions: g.divisions.filter(o => has(o.company_name, search)),
          },
    ),
    ...standalone
      .filter(o => !search || has(o.company_name, search))
      .map(o => ({ divisions: [o] })),
  ].filter(g => g.divisions.length > 0);

  const isCurrent = (g: OrgGroup) => g.divisions.some(o => o._id === currentId);
  let room = orgListLimit - (matching.find(isCurrent)?.divisions.length ?? 0);
  return matching
    .map(g => {
      if (isCurrent(g)) return g;
      const divisions = g.divisions.slice(0, Math.max(room, 0));
      room -= divisions.length;
      return { ...g, divisions };
    })
    .filter(g => g.divisions.length > 0);
}

/** Division rows a list of groups draws. */
export const countRows = (groups: OrgGroup[]) =>
  groups.reduce((n, g) => n + g.divisions.length, 0);
